import { useState, type ReactNode } from 'react'
import {
  ActionIcon,
  Badge,
  Button,
  Divider,
  Group,
  MultiSelect,
  Popover,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import {
  IconCalendar,
  IconFilter,
  IconSortAscending,
  IconSortDescending,
} from '@tabler/icons-react'
import { BOARD_PRIORITY_VALUES, BOARD_STATUS_VALUES } from '@behuman/core-contract'
import type { CellRef } from '@/pages/Boards/hooks'
import {
  activeFilterCount,
  DEFAULT_QUEUE_FILTERS,
  DEFAULT_QUEUE_SORT,
  ME_ASSIGNEE,
  UNASSIGNED_ASSIGNEE,
  type QueueAiFilter,
  type QueueFilters,
  type QueueSort,
} from './queueFilters'

// Which column-backed controls this board supports. A control whose column is
// absent can't filter/sort anything, so it's hidden rather than shown active-
// but-inert (Source is not column-backed, so it always shows).
type QueueFilterColumns = {
  dueDate: boolean
  status: boolean
  priority: boolean
  assignee: boolean
}

type QueueFilterPanelProps = {
  filters: QueueFilters
  sort: QueueSort
  assignees: CellRef[]
  columns: QueueFilterColumns
  onFiltersChange: (filters: QueueFilters) => void
  onSortChange: (sort: QueueSort) => void
}

// Every dropdown nested in this panel (the calendar and the comboboxes) must render
// INSIDE the panel's own DOM. Mantine closes a Popover when a mousedown's composedPath()
// contains neither its target nor its dropdown node, so a portaled child dropdown reads
// as an outside click and would dismiss the whole panel on every single selection —
// picking a two-date range became impossible. Rendering in place keeps the panel open;
// Popover.Dropdown sets no overflow, so nothing gets clipped.
const NESTED_DROPDOWN = { withinPortal: false } as const

// One label + control stacked, so the popover reads as a tidy vertical form.
const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <Stack gap={4}>
    <Text size="xs" fw={600} c="dimmed" tt="uppercase">
      {label}
    </Text>
    {children}
  </Stack>
)

export const QueueFilterPanel = ({
  filters,
  sort,
  assignees,
  columns,
  onFiltersChange,
  onSortChange,
}: QueueFilterPanelProps) => {
  const [opened, setOpened] = useState(false)
  const count = activeFilterCount(filters, {
    status: columns.status,
    priority: columns.priority,
    assignee: columns.assignee,
    dueDate: columns.dueDate,
  })
  const setField = (patch: Partial<QueueFilters>) => onFiltersChange({ ...filters, ...patch })

  const assigneeData = [
    {
      group: 'Quick',
      items: [
        { value: ME_ASSIGNEE, label: 'Me' },
        { value: UNASSIGNED_ASSIGNEE, label: 'Unassigned' },
      ],
    },
    ...(assignees.length > 0
      ? [
          {
            group: 'People',
            items: assignees.map((a) => ({ value: a.reference, label: a.display })),
          },
        ]
      : []),
  ]

  const clearAll = () => {
    onFiltersChange(DEFAULT_QUEUE_FILTERS)
    onSortChange(DEFAULT_QUEUE_SORT)
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom-end"
      shadow="md"
      withinPortal
      trapFocus
      // PD-1395: pin the panel's width through Mantine's own prop. The dropdown
      // carries an inline `width: max-content`, which outranks a Tailwind class,
      // so the old `w-72` never applied and the panel widened to fit its longest
      // row — with enough assignee chips that pushed Clear all out of reach.
      // Constrained, the chips wrap instead (they already may) and every control
      // stays put however many are selected.
      width={288}
    >
      <Popover.Target>
        <Button
          size="sm"
          radius={7}
          variant={count > 0 ? 'light' : 'default'}
          onClick={() => setOpened((o) => !o)}
          leftSection={<IconFilter size={14} />}
          rightSection={
            count > 0 ? (
              <Badge size="xs" circle variant="filled">
                {count}
              </Badge>
            ) : null
          }
        >
          Filter
        </Button>
      </Popover.Target>
      <Popover.Dropdown p="md">
        <Stack gap="sm">
          {columns.dueDate && (
            <>
              <Field label="Sort">
                <Group gap="xs" wrap="nowrap">
                  <Select
                    size="xs"
                    flex={1}
                    aria-label="Sort by"
                    data={[
                      { value: 'default', label: 'Default order' },
                      { value: 'dueDate', label: 'Due date' },
                    ]}
                    value={sort.key}
                    onChange={(key) =>
                      onSortChange({ ...sort, key: key === 'dueDate' ? 'dueDate' : 'default' })
                    }
                    allowDeselect={false}
                    comboboxProps={NESTED_DROPDOWN}
                  />
                  {sort.key === 'dueDate' && (
                    <ActionIcon
                      size="lg"
                      variant="default"
                      aria-label={sort.direction === 'asc' ? 'Soonest first' : 'Latest first'}
                      onClick={() =>
                        onSortChange({
                          ...sort,
                          direction: sort.direction === 'asc' ? 'desc' : 'asc',
                        })
                      }
                    >
                      {sort.direction === 'asc' ? (
                        <IconSortAscending size={16} />
                      ) : (
                        <IconSortDescending size={16} />
                      )}
                    </ActionIcon>
                  )}
                </Group>
              </Field>
              <Divider />

              <Field label="Due date">
                <DatePickerInput
                  type="range"
                  size="xs"
                  aria-label="Filter by due date range"
                  placeholder="Any due date"
                  leftSection={<IconCalendar size={14} />}
                  // Matches the year-bearing due dates in the table (PD-1684) — a range
                  // reading "Sep 18 – Sep 18" says nothing about which year is filtered.
                  valueFormat="MMM D, YYYY"
                  // A single click picks a one-day window (both ends the same day)
                  // rather than leaving the range half-open.
                  allowSingleDateInRange
                  clearable
                  value={[filters.dueFrom, filters.dueTo]}
                  onChange={([dueFrom, dueTo]) => setField({ dueFrom, dueTo })}
                  popoverProps={NESTED_DROPDOWN}
                />
              </Field>
            </>
          )}

          {columns.assignee && (
            <Field label="Assignee">
              <MultiSelect
                size="xs"
                aria-label="Filter by assignee"
                placeholder={filters.assignees.length ? undefined : 'Anyone'}
                data={assigneeData}
                value={filters.assignees}
                onChange={(selected) => setField({ assignees: selected })}
                searchable
                clearable
                comboboxProps={NESTED_DROPDOWN}
              />
            </Field>
          )}

          {columns.priority && (
            <Field label="Priority">
              <MultiSelect
                size="xs"
                aria-label="Filter by priority"
                placeholder={filters.priorities.length ? undefined : 'Any priority'}
                data={[...BOARD_PRIORITY_VALUES]}
                value={filters.priorities}
                onChange={(priorities) => setField({ priorities })}
                clearable
                comboboxProps={NESTED_DROPDOWN}
              />
            </Field>
          )}

          {columns.status && (
            <Field label="Status">
              <Select
                size="xs"
                aria-label="Filter by status"
                placeholder="Any status"
                data={[...BOARD_STATUS_VALUES]}
                value={filters.status}
                onChange={(status) => setField({ status })}
                clearable
                comboboxProps={NESTED_DROPDOWN}
              />
            </Field>
          )}

          <Field label="Source">
            <SegmentedControl
              size="xs"
              aria-label="Filter by source"
              fullWidth
              data={[
                { value: 'all', label: 'All' },
                { value: 'ai', label: 'AI-generated' },
                { value: 'human', label: 'Human-created' },
              ]}
              value={filters.aiGenerated}
              onChange={(value) => setField({ aiGenerated: value as QueueAiFilter })}
            />
          </Field>

          <Divider />

          <Group justify="flex-end">
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              disabled={count === 0 && sort.key === 'default'}
              onClick={clearAll}
            >
              Clear all
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
